import { useState, useEffect, useContext } from 'react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart, Pie } from 'recharts';
import api from '../services/api';
import { Download, Calendar, TrendingUp, DollarSign, FileText, Clock, Users, PieChart as PieIcon, ArrowUpRight, Award } from 'lucide-react';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { SocketContext } from '../contexts/SocketContext';
import EodSummary from '../components/EodSummary';

export default function Reports() {
  const [salesData, setSalesData] = useState([]);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [hourlyData, setHourlyData] = useState([]);
  const [staffData, setStaffData] = useState([]);
  const [marginData, setMarginData] = useState([]);
  const { theme } = useContext(ThemeContext);
  const { settings, currencySymbol } = useSettings();
  const { socket } = useContext(SocketContext);
  const [showEodModal, setShowEodModal] = useState(false);

  const loadData = async () => {
    try {
      const [salesRes, hourlyRes, staffRes, marginRes] = await Promise.all([
        api.get('/sales'),
        api.get(`/reports/performance/hourly?timezoneOffset=${new Date().getTimezoneOffset()}`),
        api.get(`/reports/performance/staff?timezoneOffset=${new Date().getTimezoneOffset()}`),
        api.get('/reports/performance/margins')
      ]);

      // Daily aggregation
      let total = 0;
      const agg = salesRes.data.reduce((acc, s) => {
        const day = new Date(s.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        acc[day] = (acc[day] || 0) + s.total;
        total += s.total;
        return acc;
      }, {});
      
      setSalesData(Object.entries(agg).map(([d, t]) => ({ date: d, total: t })));
      setTotalRevenue(total);
      setHourlyData(hourlyRes.data);
      setStaffData(staffRes.data);
      setMarginData(marginRes.data);
    } catch (err) {
      console.error(err);
    }
  };

  const exportToCSV = () => {
    let csv = "Category,Revenue,Net Profit,Margin %\n";
    marginData.forEach(cat => {
      csv += `"${cat.name}",${cat.revenue},${cat.profit},${cat.margin}\n`;
    });
    
    csv += "\nStaff Member,Orders,Sales Volume,Avg Order\n";
    staffData.forEach(staff => {
      csv += `"${staff.name}",${staff.orders},${staff.sales},${staff.avgOrder}\n`;
    });

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Enterprise_Report_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  useEffect(() => { loadData(); }, []);

  useEffect(() => {
    if (socket) {
      socket.on('new_sale', loadData);
      socket.on('stock_update', loadData);
      return () => {
        socket.off('new_sale');
        socket.off('stock_update');
      };
    }
  }, [socket]);



  const chartColors = {
    grid: theme === 'nexus' ? '#1e293b' : '#f1f5f9',
    text: theme === 'nexus' ? '#94a3b8' : '#64748b',
    brand: '#6366f1',
    teal: '#14b8a6',
    rose: '#f43f5e',
    amber: '#f59e0b',
    pie: ['#6366f1', '#14b8a6', '#f59e0b', '#f43f5e', '#8b5cf6', '#ec4899']
  };

  return (
    <div className="space-y-8 animate-fade-in-up max-w-7xl mx-auto pb-10">
      <div className="flex justify-between items-end mb-6">
        <div>
          <h2 className="text-3xl font-black text-theme-main uppercase tracking-tight">Performance Intelligence</h2>
          <p className="text-theme-muted mt-1 font-bold uppercase tracking-widest text-[10px]">Real-time analytics across staff, sales, and margins.</p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={() => setShowEodModal(true)}
            className="bg-emerald-500 hover:bg-emerald-600 text-white px-5 py-3 rounded-2xl shadow-xl font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all active:scale-95"
          >
            <FileText size={18} /> EOD Snapshot
          </button>
          <button 
            onClick={exportToCSV}
            className="bg-theme-card border border-theme hover:border-brand text-theme-main px-5 py-3 rounded-2xl shadow-sm font-black text-xs uppercase tracking-widest flex items-center gap-2 transition-all"
          >
            <Download size={18} /> Export Data
          </button>
        </div>
      </div>

      {/* Summary Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {[
          { label: 'Total Volume', value: `${currencySymbol}${totalRevenue.toLocaleString()}`, icon: DollarSign, color: 'brand', bg: 'bg-brand/10', text: 'text-brand' },
          { label: 'Avg Sale', value: `${currencySymbol}${salesData.length > 0 ? (totalRevenue / salesData.length).toFixed(2) : '0.00'}`, icon: TrendingUp, color: 'teal', bg: 'bg-emerald-500/10', text: 'text-emerald-500' },
          { label: 'Top Staff', value: staffData[0]?.name || '---', icon: Award, color: 'amber', bg: 'bg-amber-500/10', text: 'text-amber-500' },
          { label: 'Peak Hour', value: hourlyData.reduce((p, c) => (c.sales > p.sales ? c : p), hourlyData[0] || {}).hour || '---', icon: Clock, color: 'rose', bg: 'bg-rose-500/10', text: 'text-rose-500' }
        ].map((kpi, idx) => (
          <div key={idx} className="glass rounded-3xl p-6 border border-white/10 hover:border-brand/50 transition-all duration-500 group relative overflow-hidden">
            <div className={`absolute top-0 right-0 w-24 h-24 ${kpi.bg} blur-3xl -mr-8 -mt-8 opacity-50 group-hover:opacity-100 transition-opacity`} />
            <div className="flex justify-between items-start mb-4 relative z-10">
              <div className={`p-3 rounded-2xl ${kpi.bg} ${kpi.text} group-hover:scale-110 transition-transform shadow-lg`}>
                <kpi.icon size={24} />
              </div>
              <ArrowUpRight className="text-theme-muted opacity-30" size={16} />
            </div>
            <p className="text-theme-muted text-[10px] font-black uppercase tracking-widest mb-1 relative z-10">{kpi.label}</p>
            <h3 className="text-2xl font-black text-theme-main tracking-tight relative z-10">{kpi.value}</h3>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Hourly Heatmap */}
        <div className="lg:col-span-2 glass p-8 rounded-3xl border border-white/10 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand to-transparent opacity-50" />
          <div className="flex justify-between items-center mb-8 relative z-10">
            <h3 className="text-sm font-black text-theme-muted uppercase tracking-[0.2em] flex items-center gap-2">
              <Clock size={16} className="text-brand" /> Hourly Traffic Heatmap
            </h3>
            <div className="flex items-center gap-2">
               <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
               <span className="text-[10px] font-black text-emerald-500 uppercase">Live Intelligence</span>
            </div>
          </div>
          <div className="h-[300px] w-full relative z-10">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={hourlyData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={chartColors.brand} stopOpacity={0.4}/>
                    <stop offset="95%" stopColor={chartColors.brand} stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chartColors.grid} strokeOpacity={0.5} />
                <XAxis dataKey="hour" axisLine={false} tickLine={false} tick={{fill: chartColors.text, fontSize: 9, fontWeight: 'bold'}} />
                <YAxis axisLine={false} tickLine={false} tick={{fill: chartColors.text, fontSize: 9, fontWeight: 'bold'}} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(15, 23, 42, 0.9)', 
                    backdropFilter: 'blur(12px)',
                    borderRadius: '20px', 
                    border: '1px solid rgba(255,255,255,0.1)', 
                    boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                    padding: '12px'
                  }}
                  itemStyle={{ color: '#fff', fontWeight: '900', fontSize: '12px', textTransform: 'uppercase' }}
                  labelStyle={{ color: 'rgba(255,255,255,0.5)', fontWeight: 'bold', fontSize: '10px', marginBottom: '4px' }}
                />
                <Area type="monotone" dataKey="sales" stroke={chartColors.brand} strokeWidth={4} fillOpacity={1} fill="url(#colorSales)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Staff Leaderboard */}
        <div className="glass p-8 rounded-3xl border border-white/10 flex flex-col relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-brand/5 blur-3xl rounded-full -mr-16 -mt-16" />
          <h3 className="text-sm font-black text-theme-muted uppercase tracking-[0.2em] mb-8 flex items-center gap-2 relative z-10">
            <Users size={16} className="text-brand" /> Top Performers
          </h3>
          <div className="space-y-6 flex-1 relative z-10">
            {staffData.map((staff, i) => (
              <div key={i} className="flex items-center justify-between group p-3 rounded-2xl hover:bg-white/5 transition-all duration-300">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center font-black text-xs shadow-lg ${
                    i === 0 ? 'bg-gradient-to-br from-amber-400 to-amber-600 text-white' : 
                    i === 1 ? 'bg-gradient-to-br from-slate-300 to-slate-500 text-white' :
                    'bg-theme-main/50 text-theme-muted border border-white/5'
                  }`}>
                    {i + 1}
                  </div>
                  <div>
                    <p className="text-xs font-black text-theme-main uppercase tracking-tight group-hover:text-brand transition-colors">{staff.name}</p>
                    <p className="text-[10px] text-theme-muted font-bold uppercase tracking-widest">{staff.orders} Orders</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-sm font-black text-theme-main">{currencySymbol}{staff.sales.toFixed(0)}</p>
                  <div className="flex items-center justify-end gap-1 mt-0.5">
                    <TrendingUp size={10} className="text-emerald-500" />
                    <p className="text-[10px] text-emerald-500 font-black uppercase">${staff.avgOrder.toFixed(0)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Margin Analysis */}
        <div className="lg:col-span-3 glass p-8 rounded-3xl border border-white/10 relative overflow-hidden">
          <div className="absolute bottom-0 right-0 w-64 h-64 bg-emerald-500/5 blur-[100px] rounded-full -mb-32 -mr-32" />
          <div className="flex justify-between items-center mb-8 relative z-10">
            <h3 className="text-sm font-black text-theme-muted uppercase tracking-[0.2em] flex items-center gap-2">
              <PieIcon size={16} className="text-brand" /> Category Yield & Profitability
            </h3>
            <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest bg-white/5 px-3 py-1 rounded-full border border-white/5">Market Analysis</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {marginData.map((cat, i) => (
              <div key={i} className="p-6 rounded-3xl bg-theme-main/30 border border-white/5 hover:border-brand/40 transition-all duration-300 group">
                <div className="flex justify-between items-start mb-4">
                  <span className="text-[10px] font-black text-theme-muted uppercase tracking-widest group-hover:text-brand transition-colors">{cat.name}</span>
                  <div className={`px-2 py-1 rounded-lg text-[9px] font-black uppercase shadow-sm ${
                    cat.margin > 40 ? 'bg-emerald-500/20 text-emerald-500 border border-emerald-500/20' : 
                    cat.margin > 20 ? 'bg-amber-500/20 text-amber-500 border border-amber-500/20' : 'bg-rose-500/20 text-rose-500 border border-rose-500/20'
                  }`}>
                    {cat.margin}% Margin
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="flex justify-between items-end">
                    <div>
                      <p className="text-[9px] text-theme-muted font-black uppercase mb-1">Total Revenue</p>
                      <p className="text-lg font-black text-theme-main">{currencySymbol}{cat.revenue.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="pt-4 border-t border-white/5 flex justify-between items-center">
                    <p className="text-[9px] text-theme-muted font-black uppercase">Net Profit</p>
                    <p className="text-base font-black text-emerald-500">{currencySymbol}{cat.profit.toLocaleString()}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {showEodModal && (
        <EodSummary 
          onClose={() => setShowEodModal(false)} 
          currencySymbol={currencySymbol} 
        />
      )}
    </div>
  );
}